import { _ } from "~/i18n";
import Scene from "~/scene";
import SceneMarker from "./scene/marker/marker";
import SceneLayerGeometry from "./scene/layer/geometry";
import SceneLayerMarker from "./scene/layer/marker";
import SceneCoords from "./scene/common/coords";
import ScenePixel from "./scene/common/pixel";
import SceneMarkerMapyWindy from "./scene/marker/mapy-windy";
import PUBTMap from "~/route4/pt/map";
import { getLocation } from "~/whereami";
import { standardDistanceFormat } from "~/util/format";
import { store } from "~/state/store";
import { watch, watchCoordinates } from "~/state/util";
import { Unsubscribe } from "redux";

interface IMapLayersObject {
	activeGeomLayer: SceneLayerGeometry;
	geomLayer?: SceneLayerGeometry;
	hoverGeomLayer?: SceneLayerGeometry;
	markers: Array<SceneMarker>;
	markerLayer: SceneLayerMarker;
	[key: string]: any;
}

interface IFraction {
	fraction: number;
	bestPoint: ScenePixel;
	index: number;
	bestDistance: number;
	distance: number;
}

/**
 * Obal pro:
 * - popup na geometrii
 *
 * Signaly na window:
 * - map-marker-show { detail: { frac: 0-1 }}
 * - map-marker-hide
 */
export default class MapInteractiveGeometry {
	_map: IMapLayersObject | PUBTMap;

	_dom: {
		[key: string]: HTMLElement;
	};

	_options: {
		[key: string]: boolean | number | string;
	};

	_enabled: boolean;

	_totalLength: number;

	_allCoords: Array<Array<Array<SceneCoords>>>;

	_allCoordsItems: Array<Array<SceneCoords>>;

	_allPixels: Array<Array<ScenePixel>>;

	_dndMarkerAppend : boolean;

	_dndMarker: SceneMarkerMapyWindy;

	_markerEvents: Array<any>;

	_sceneSignals: Array<any>;

	_lastPointFraction: IFraction;

	_listenerSet: {
		activeGeom: boolean;
		geom: boolean;
		hoverGeom: boolean;
	};

	_storeUnsubscribe: Unsubscribe;

	constructor(map: IMapLayersObject | PUBTMap, opts?: {
		[key: string]: boolean | number | string;
	}) {
		this._map = map;
		// pro potreby card, zobrazovani
		this._dom = {
			cardFromStart: Mapy.Util.node("div"),
			cardToEnd: Mapy.Util.node("div"),
		};
		this._options = {
			dragAndDrop: true,
			fromRoute: false,
			...opts,
		};
		this._enabled = true;
		this._totalLength = 0;
		this._allCoords = [];
		this._allCoordsItems = [];
		this._allPixels = [];
		this._dndMarkerAppend = false;
		this._dndMarker = Scene.createMarkerWindy(null, "route-dnd-marker", {
			type: Scene.VISUAL.MARKER_WINDY.LITTLE,
		});
		if (this._options.dragAndDrop) {
			this._dndMarker.decorateDraggable();
			this._dndMarker.setStyle("cursor", "pointer");
		}
		this._dndMarker.setCardOptions({
			cb: card => {
				this._setPopupCont(card);
			},
			showTimeout: 0,
			hideTimeout: 0,
		});
		this._markerEvents = [];
		this._sceneSignals = [];
		const wt = watch(store.getState, "map", watchCoordinates);

		this._storeUnsubscribe = store.subscribe(wt((newMap, oldMap) => {
			if (
				newMap.longitude !== oldMap.longitude
				|| newMap.latitude !== oldMap.latitude
				|| newMap.zoom !== oldMap.zoom
			) {
				this._mapRedraw();
			}
		}));
		this._lastPointFraction = null;
		this._listenerSet = {
			activeGeom: false,
			geom: false,
			hoverGeom: false,
		};
	}

	destroy() {
		this._storeUnsubscribe?.();
		this._storeUnsubscribe = null;

		this._listenerSet = {
			activeGeom: false,
			geom: false,
			hoverGeom: false,
		};
	}

	/**
	 * Start interaktivity.
	 *
	 * @param totalLength Celkova delka trasy
	 */
	start(totalLength: number) {
		this._totalLength = totalLength;
		this._updatePixels();

		if (this._map.activeGeomLayer && !this._listenerSet.activeGeom) {
			this._map.activeGeomLayer.addEventListener("mouseover", () => {
				this._markerEvents.push(JAK.Events.addListener(Scene.getContainer(), "mousemove", this, "_geomMouseMove"));
				this._sceneSignals.push(Scene.addListener("marker-click", "_markerDragStop", this, this._dndMarker));
				this._sceneSignals.push(Scene.addListener("marker-drag-start", "_markerDragStart", this, this._dndMarker));
				this._sceneSignals.push(Scene.addListener("marker-drag-stop", "_markerDragStop", this, this._dndMarker));
			});

			this._listenerSet.activeGeom = true;
		}

		if (this._map.geomLayer && !this._listenerSet.geom) {
			this._map.geomLayer.addEventListener("mouseover", () => {
				this._markerEvents.push(JAK.Events.addListener(Scene.getContainer(), "mousemove", this, "_geomMouseMove"));
			});

			this._listenerSet.geom = true;
		}

		if (this._map.hoverGeomLayer && !this._listenerSet.hoverGeom) {
			this._map.hoverGeomLayer.addEventListener("mouseout", () => {
				if (this._options.fromRoute) {
					Mapy.CML.state = {
						route: {
							hoverIndex: Mapy.CML.state.route.index,
						},
					};
				}
			});

			this._map.hoverGeomLayer?.addEventListener("click", () => {
				if (this._options.fromRoute) {
					Mapy.CML.state = {
						route: {
							index: Mapy.CML.state.route.hoverIndex,
						},
					};
				}

				Mapy.getComponent("history").save();
			});

			this._listenerSet.hoverGeom = true;
		}
	}

	set enabled(state: boolean) {
		this._enabled = state;
		if (!state) {
			this._hide();
		}
	}

	set markerDad(state: boolean) {
		if (this._options.dragAndDrop) {
			this._dndMarker.setDrag(state);
		}
	}

	/**
	 * Vycisteni dat.
	 */
	clear() {
		this._allCoords.length = 0;
		this._allCoordsItems.length = 0;
		this._allPixels.length = 0;
		this._totalLength = 0;
	}

	/**
	 * Pridani pole souradnic.
	 *
	 * @param coords
	 */
	addCoords(index: number, coords: Array<SceneCoords>) {
		if (!this._allCoords[index]) {
			this._allCoords[index] = [];
			this._allCoordsItems[index] = [];
		}
		this._allCoords[index].push(coords);
		coords.forEach(item => this._allCoordsItems[index].push(item));
	}

	get allCoords() {
		return this._allCoordsItems;
	}

	get allPixels() {
		return this._allPixels;
	}

	showOnCoords(coords: SceneCoords) {
		this._geomMouseMove(coords);
	}

	hide() {
		this._hide();
	}

	/**
	 * Pohyb mysi pres geometrii.
	 *
	 * @param event
	 */
	_geomMouseMove(event: any, target?: HTMLElement) {
		if (!this._enabled) {
			return;
		}

		const point = target ? Scene.pixelFromEvent(event) : event.toPixel();
		const index = this._options.fromRoute ? Mapy.CML.state.route.index : 0;

		if (!this._allPixels[index]) {
			return;
		}

		const res = Mapy.Util.getPointAndFraction(this._allPixels[index], point);

		if (res.bestDistance > 10) {
			let hover = false;

			this._hide();

			for (let pixel = 0; pixel < this._allPixels.length; pixel++) {
				const allPixelsItem = this._allPixels[pixel];

				if (pixel !== index) {
					const pointFraction = Mapy.Util.getPointAndFraction(allPixelsItem, point);

					if (pointFraction.bestDistance < 5) {
						hover = true;

						if (this._options.fromRoute) {
							Mapy.CML.state = {
								route: {
									hoverIndex: pixel,
								},
							};
						}
						break;
					}
				}
			}

			if (this._options.fromRoute && !hover) {
				Mapy.CML.state = {
					route: {
						hoverIndex: index,
					},
				};
			}

			return;
		}

		if (this._options.fromRoute) {
			Mapy.CML.state = {
				route: {
					hoverIndex: index,
				},
			};
		}

		this._lastPointFraction = res;
		this._dndMarker.setCoords(Scene.pixelToCoords(res.bestPoint));

		if (!this._dndMarkerAppend) {
			this._map.markerLayer.addMarker(this._dndMarker);
			this._dndMarkerAppend = true;
			this._map.markers.forEach(marker => {
				marker.setStyle("opacity", "0.7");
				marker.setStyle("pointer-events", "none");
			});
		}

		this._updateCardBody(res.distance);
		this._dndMarker.showCard();
		this._dndMarker.moveCard();
		window.dispatchEvent(new CustomEvent("map-marker-show", { detail: { frac: res.fraction } }));
	}

	/**
	 * Prekresleni mapy.
	 */
	_mapRedraw() {
		this._updatePixels();
	}

	/**
	 * Update coords -> pixels.
	 */
	_updatePixels() {
		this._allPixels.length = 0;
		if (this._allCoordsItems.length) {
			this._allCoordsItems.forEach(ac => {
				if (ac.length) {
					this._allPixels.push(ac.map(item => Scene.coordsToPixel(item)));
				} else {
					this._allPixels.push([]);
				}
			});
		}
	}

	/**
	 * Nastaveni popup karty.
	 *
	 * @param card
	 */
	_setPopupCont(card: any) {
		const cardBody = card.getBody();

		cardBody.appendChild(this._dom.cardFromStart);
		cardBody.appendChild(this._dom.cardToEnd);
		card.getContainer().classList.add("route-dnd-popup");
		card.setSize(180);
	}

	_updateCardBody(distance: number) {
		// vypocet obsahu card (vzdalenost od/do)

		const fromStart = distance > 0
			? standardDistanceFormat(distance)
			: {
				distance: 0,
				unit: "m",
			};
		const toEnd = distance < this._totalLength
			? standardDistanceFormat(this._totalLength - distance)
			: {
				distance: 0,
				unit: "m",
			};

		this._dom.cardFromStart.innerHTML = fromStart.distance + String.fromCharCode(160);
		this._dom.cardFromStart.innerHTML += `<span>${fromStart.unit}</span>`;
		this._dom.cardFromStart.innerHTML += `<span>${_("route.geometry.marker.from").replace(" ", String.fromCharCode(160))}</span>`;
		this._dom.cardToEnd.innerHTML = toEnd.distance + String.fromCharCode(160);
		this._dom.cardToEnd.innerHTML += `<span>${toEnd.unit}</span>`;
		this._dom.cardToEnd.innerHTML += `<span>${_("route.geometry.marker.to").replace(" ", String.fromCharCode(160))}</span>`;
	}

	/**
	 * Zacatek pretahovani markeru.
	 */
	_markerDragStart() {
		const routeState = Mapy.CML.state.route;

		// nacitame -> nebudeme menit polohu
		if (Mapy.CML.state.route.loading || routeState.readOnly) {
			return;
		}

		this._dndMarker.removeCard();
		this._dndMarker.setCardOptions({
			hidden: true,
		});
		JAK.Events.removeListeners(this._markerEvents);
	}

	/**
	 * Konec tahani markeru/click.
	 */
	_markerDragStop() {
		const routeState = Mapy.CML.state.route;

		// nacitame -> nebudeme menit polohu
		if (routeState.loading || routeState.readOnly) {
			return;
		}

		this._hide();

		if (this._options.fromRoute) {
			if (routeState.items[0].tripData[Mapy.CML.state.route.index]) {
				let waypoints = [];

				if (routeState.params.waypoints) {
					waypoints = routeState.params.waypoints;
				} else {
					waypoints = routeState.items[0].tripData[Mapy.CML.state.route.index].triplist[routeState.params.tripIndex].waypoints;
				}

				const coords = Scene.stringToCoords(waypoints);
				const distances = Mapy.Util.IP.countDistances(coords);
				const wholeDistance = distances[distances.length - 1];
				const distRatio = distances.map(item => item / wholeDistance);
				let ind = -1;

				for (let index = 0, max = distRatio.length - 1; index < max; index++) {
					const cur = distRatio[index];
					const next = distRatio[index + 1];

					if (this._lastPointFraction.fraction >= cur && this._lastPointFraction.fraction <= next) {
						ind = index;
						break;
					}
				}

				if (ind !== -1) {
					const markerCoords = this._dndMarker.getCoords();

					coords.splice(ind + 1, 0, markerCoords);

					const route: {
						[key: string]: any;
					} = {
						params: {
							waypoints: Scene.coordsToString(coords),
							tripIndex: 0,
						},
					};

					if (Mapy.CML.state.route.likeData) {
						if ("owner_name" in Mapy.CML.state.route.likeData) {
							Mapy.CML.state = {
								route: {
									likeData: null,
									likeDataChange: false,
								},
							};

							Mapy.getComponent("dimcurator").setState();
						} else {
							route.likeDataChange = true;
						}
					}
					// nastavime do params
					Mapy.CML.state = {
						route,
					};
				}
			} else {
				// zjistime mezi ktere itemy budeme vkladat
				let fromInd = 0;
				let toInd = 0;
				let findInd = -1;

				for (let index = 0, max = this._allCoords[Mapy.CML.state.route.index].length; index < max; index++) {
					toInd += this._allCoords[Mapy.CML.state.route.index][index].length;
					if (this._lastPointFraction.index >= fromInd && this._lastPointFraction.index <= toInd) {
						findInd = index;
						break;
					}
					fromInd = toInd;
				}

				const coords = this._dndMarker.getCoords();

				getLocation(coords).then(data => {
					this.clear();

					const items = routeState.items;

					items.splice(findInd + 1, 0, {
						id: data.id,
						source: data.source,
						geometry: Scene.coordsToString([coords]),
						title: data.label,
						desc: "",
					});
					Mapy.CML.state = {
						route: {
							items,
						},
					};
				}, () => {});
			}
		}
	}

	/**
	 * Skryti markeru.
	 */
	_hide() {
		this._dndMarker.removeCard();

		if (this._dndMarkerAppend) {
			this._map.markerLayer.removeMarker(this._dndMarker);
			this._dndMarkerAppend = false;
			this._map.markers.forEach(marker => {
				marker.setStyle("opacity", "");
				marker.setStyle("pointer-events", "");
			});
		}
		Scene.removeListeners(this._sceneSignals);
		JAK.Events.removeListeners(this._markerEvents);
		window.dispatchEvent(new CustomEvent("map-marker-hide", { detail: null }));
	}
}
